import { useState, useEffect, useRef } from 'react';
import { api, PlatformAdmin, AdminRole } from '../api';
import { getErrorMessage } from '../../../shared/src';

interface AdminsPageProps {
  currentAdminRole: AdminRole;
  currentAdminId: string;
}

// Role definitions for the dropdown
const ADMIN_ROLES = [
  { value: 'super_admin' as AdminRole, label: 'Super Admin', description: 'Full access + manage all admins' },
  { value: 'admin_editor' as AdminRole, label: 'Editor', description: 'Org management + manage viewers' },
  { value: 'admin_viewer' as AdminRole, label: 'Viewer', description: 'Read-only access' },
];

export function AdminsPage({ currentAdminRole, currentAdminId }: AdminsPageProps) {
  const [admins, setAdmins] = useState<PlatformAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<PlatformAdmin | null>(null);

  // Inline role dropdown state
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [updatingAdminId, setUpdatingAdminId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Form state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<AdminRole>('admin_viewer');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadAdmins();
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdownId(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadAdmins = async () => {
    try {
      const result = await api.listAdmins();
      setAdmins(result.admins);
    } catch (err) {
      setError('Failed to load admins');
      console.error('Failed to load admins:', err);
    } finally {
      setLoading(false);
    }
  };

  const getAllowedRoles = (): AdminRole[] => {
    if (currentAdminRole === 'super_admin') {
      return ['super_admin', 'admin_editor', 'admin_viewer'];
    }
    if (currentAdminRole === 'admin_editor') {
      return ['admin_viewer'];
    }
    return [];
  };

  const canManageAdmin = (admin: PlatformAdmin): boolean => {
    if (admin.id === currentAdminId) return false;
    if (currentAdminRole === 'super_admin') return true;
    if (currentAdminRole === 'admin_editor') return admin.role === 'admin_viewer';
    return false;
  };

  // Check if current admin can change target admin to a specific role
  const canChangeToRole = (targetRole: AdminRole): boolean => {
    if (currentAdminRole === 'super_admin') return true;
    if (currentAdminRole === 'admin_editor') return targetRole === 'admin_viewer';
    return false;
  };

  const handleInlineRoleChange = async (admin: PlatformAdmin, newRole: AdminRole) => {
    if (newRole === admin.role) {
      setOpenDropdownId(null);
      return;
    }

    setOpenDropdownId(null);
    setUpdatingAdminId(admin.id);
    setError('');

    try {
      await api.updateAdmin(admin.id, { role: newRole });
      setSuccess(`Role updated to ${getRoleLabel(newRole)}`);
      loadAdmins();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setUpdatingAdminId(null);
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'super_admin': return 'Super Admin';
      case 'admin_editor': return 'Editor';
      case 'admin_viewer': return 'Viewer';
      default: return role;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'green';
      case 'invited': return 'yellow';
      case 'suspended': return 'red';
      default: return 'gray';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      await api.inviteAdmin({
        email: inviteEmail,
        name: inviteName || undefined,
        role: inviteRole,
      });
      setSuccess(`Invitation sent to ${inviteEmail}`);
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteName('');
      setInviteRole('admin_viewer');
      loadAdmins();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedAdmin) return;
    setError('');
    setSaving(true);

    try {
      await api.deleteAdmin(selectedAdmin.id);
      setSuccess(`Admin ${selectedAdmin.email} deleted`);
      setShowDeleteModal(false);
      setSelectedAdmin(null);
      loadAdmins();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleResendInvitation = async (admin: PlatformAdmin) => {
    setError('');
    try {
      await api.resendAdminInvitation(admin.id);
      setSuccess(`Invitation resent to ${admin.email}`);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const openDeleteModal = (admin: PlatformAdmin) => {
    setSelectedAdmin(admin);
    setShowDeleteModal(true);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  const allowedRoles = getAllowedRoles();

  return (
    <div className="page">
      <header className="page-header">
        <h1>Platform Admins</h1>
        {allowedRoles.length > 0 && (
          <button className="btn-primary" onClick={() => setShowInviteModal(true)}>
            Invite Admin
          </button>
        )}
      </header>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <table className="data-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Name</th>
            <th>Role</th>
            <th>Status</th>
            <th>Last Login</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {admins.map((admin) => (
            <tr key={admin.id}>
              <td>
                {admin.email}
                {admin.id === currentAdminId && (
                  <span className="badge-you">(you)</span>
                )}
              </td>
              <td>{admin.name || '-'}</td>
              <td>
                {canManageAdmin(admin) ? (
                  <div className="role-selector" ref={openDropdownId === admin.id ? dropdownRef : null}>
                    <button
                      className={`role-selector-trigger role-${admin.role}`}
                      onClick={() => setOpenDropdownId(openDropdownId === admin.id ? null : admin.id)}
                      disabled={updatingAdminId === admin.id}
                    >
                      <span className="role-label">
                        {updatingAdminId === admin.id ? 'Updating...' : getRoleLabel(admin.role)}
                      </span>
                      <svg className="chevron" width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    {openDropdownId === admin.id && (
                      <div className="role-selector-dropdown">
                        {ADMIN_ROLES.filter(role => canChangeToRole(role.value)).map((role) => (
                          <button
                            key={role.value}
                            className={`role-option ${role.value === admin.role ? 'selected' : ''}`}
                            onClick={() => handleInlineRoleChange(admin, role.value)}
                          >
                            <div className="role-option-content">
                              <span className="role-option-label">{role.label}</span>
                              <span className="role-option-description">{role.description}</span>
                            </div>
                            {role.value === admin.role && (
                              <svg className="check-icon" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M11.5 4L5.5 10L2.5 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <span className={`role-badge role-${admin.role}`}>
                    {getRoleLabel(admin.role)}
                  </span>
                )}
              </td>
              <td>
                <span className={`status-badge ${getStatusColor(admin.status)}`}>
                  {getStatusLabel(admin.status)}
                </span>
              </td>
              <td>
                {admin.last_login_at
                  ? new Date(admin.last_login_at).toLocaleString()
                  : '-'}
              </td>
              <td className="actions">
                {canManageAdmin(admin) && (
                  <>
                    {admin.status === 'invited' && (
                      <button
                        className="btn-icon"
                        onClick={() => handleResendInvitation(admin)}
                        title="Resend Invitation"
                      >
                        Resend
                      </button>
                    )}
                    <button
                      className="btn-icon danger"
                      onClick={() => openDeleteModal(admin)}
                      title="Delete"
                    >
                      Delete
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
          {admins.length === 0 && (
            <tr>
              <td colSpan={6} className="empty-state">No admins found</td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Invite Admin</h2>
            <form onSubmit={handleInvite}>
              <div className="form-group">
                <label htmlFor="inviteEmail">Email *</label>
                <input
                  type="email"
                  id="inviteEmail"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label htmlFor="inviteName">Name</label>
                <input
                  type="text"
                  id="inviteName"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="inviteRole">Role *</label>
                <select
                  id="inviteRole"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as AdminRole)}
                  required
                >
                  {allowedRoles.map((role) => (
                    <option key={role} value={role}>
                      {getRoleLabel(role)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowInviteModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedAdmin && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Delete Admin</h2>
            <p>
              Are you sure you want to delete <strong>{selectedAdmin.email}</strong>?
              This action cannot be undone.
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={handleDelete}
                disabled={saving}
              >
                {saving ? 'Deleting...' : 'Delete Admin'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
